import tw, { styled, css } from "twin.macro";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "components/RFQ/Table";
import { Button } from "newui/components/Button";
import { Flex } from "@chakra-ui/react";
import { Icon } from "assets/Icon";
import { cn, isRFQDraft } from "utils/helpers";
import { Checkbox } from "components/RFQ/Checkbox";
import moment from "moment/moment";
import { Avatar } from "components/RFQ/Avatar";
import { SubmissionEditButton } from "components/Pages/RFQDetail/SubmissionEditButton";
import StatusBadge from "components/RFQ/StatusBadge/StatusBadge";
import { navigate } from "@reach/router";
import { useSelector } from "react-redux";
import Tooltip from "components/Tooltip";
import { ContactDetailsModal } from "components/ContactDetails/ContactDetailsModal";
import { forwardRef, useState } from "react";
import { RfxSupplierListHeader } from "./RfxSupplierListHeader";
import { RFX_TABS } from "utils/constants";
import { generatePdf } from "components/RFQ/PdfDownloadButton";
import { useCurrentCompanyId } from "hooks/auth";

const ListViewStyled = styled.div(() => [
	tw`my-8 rounded-2xl`,
	css`
		box-shadow: 0px 0px 10px -4px rgba(0, 0, 0, 0.25);
		.title {
			font-weight: 500;
			font-family: Roboto;
			font-size: 14px;
		}

		.text {
			font-family: Roboto;
			font-size: 14px;
		}
	`,
]);

const SkeletonCustom = styled.div(() => [
	tw`animate-pulse bg-gray-300 w-full h-6`,
]);

const SkeleteonRow = () => (
	<TableRow>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
		<TableCell>
			<SkeletonCustom />
		</TableCell>
	</TableRow>
);

const RFQListSubmission = forwardRef(
	(
		{
			checkedRow,
			setCheckedRow,
			totalCompany,
			currPage,
			loadList,
			onOpen,
			setTypeModal,
			rfqDetail,
			isLoading,
			currentTab,
			typeGuestName,
			isSubmitted,
			navigateCompareSelected,
			isResponseRestricted,
		},
		ref
	) => {
		const [contactModalOpen, setContactModalOpen] = useState(false);
		const [selectedCompanyForContact, setSelectedCompanyForContact] =
			useState(null);

		const { accepted, all, declined, submitted } =
			rfqDetail?.supplierInvited || {
				accepted: [],
				all: [],
				declined: [],
				submitted: [],
			};

		// this is for when draft rfx and user hasn't saved any participants yet (not from BE)
		// then we show attached suppliers with status "Prequalified"
		const isDraft = isRFQDraft(rfqDetail?.status);
		const isDraftSupplierNotSavedYet = isDraft && all.length === 0;

		const [loadingIds, setLoadingIds] = useState(new Set());

		// Helper function to get company data based on draft status
		const getCompanyData = (company, key) => {
			return isDraftSupplierNotSavedYet
				? company?.[key]
				: !company?.is_guest
					? company?.company?.[key]
					: company?.[key];
		};

		// Helper function to get company object
		const getCompanyObject = (company) => {
			return isDraftSupplierNotSavedYet
				? company
				: !company?.is_guest
					? company?.company
					: company;
		};

		const rowSelected = (rowId) =>
			checkedRow?.some((row) => row?.company.id === rowId);
		const companyId = useCurrentCompanyId();
		const TableHeadComponent = ({ title, className }) => {
			return (
				<div
					className={cn("th font-roboto text-tertiary-600", className)}
					style={{ gap: "8px", alignItems: "center" }}
				>
					{title}
				</div>
			);
		};
		const supplierList = () => {
			switch (currentTab) {
				case RFX_TABS.ALL_INVITED_SUPPLIER:
					if (isDraftSupplierNotSavedYet) {
						return rfqDetail?.supplierList?.suppliers || [];
					}
					return all;
				case RFX_TABS.ACCEPTED:
					return [...accepted];
				case RFX_TABS.DECLINED:
					return declined;
				case RFX_TABS.SUBMITTED:
					return submitted;
				default:
					return all;
			}
		};

		const checkHandler = (e, row) => {
			if (e.target.checked) {
				setCheckedRow((prevRow) => {
					let newArray = [...prevRow, row].sort((a, b) => a.id - b.id);
					return newArray;
				});
			} else {
				const newArray = checkedRow.filter((checked) => checked.id !== row.id);
				setCheckedRow(newArray);
			}
		};

		const checkNoNull = (text) => {
			if (text) {
				text = text.charAt(0).toUpperCase() + text.slice(1);
				return text + ", ";
			} else {
				return "";
			}
		};

		const dateDiffChecker = (date) => {
			const today = moment(new Date());
			const lastUpdated = moment(date);
			const diffTime = today.diff(lastUpdated, "days");
			if (diffTime <= 1) {
				return moment(date).fromNow();
			} else {
				return moment(date).format("DD MMM YYYY");
			}
		};

		const viewSubmissionHandler = (data) => {
			if (isResponseRestricted) {
				return;
			}

			navigate(
				`../../${data?.rfqId}/submission/${data?.id}/${RFX_TABS.QUESTIONNAIRE}`
			);
			setTypeModal("submission");
			onOpen();
		};

		const downloadSubmission = async (data) => {
			if (isResponseRestricted) {
				return;
			}
			setLoadingIds((prev) => new Set(prev).add(data?.id));

			generatePdf(
				"responses",
				data?.rfqId,
				data?.id,
				!isNaN(data?.guest_user_id)
			).finally(() => {
				setLoadingIds((prev) => {
					const next = new Set(prev);
					next.delete(data?.id);
					return next;
				});
			});
			setTypeModal("submission");
			onOpen();
		};

		const inviteNewSupplier = () => {
			let idSupplierList = rfqDetail?.supplierList?.id;
			let supplierListName = rfqDetail?.supplierList?.name;
			navigate(
				`/account/${companyId}/supplier-list/${idSupplierList}/${supplierListName}`
			);
		};

		const openContactModal = (company) => {
			setSelectedCompanyForContact(getCompanyObject(company));
			setContactModalOpen(true);
		};

		const closeContactModal = () => {
			setContactModalOpen(false);
			setSelectedCompanyForContact(null);
		};

		const rfxStatus = rfqDetail?.status;
		const suppliers = supplierList();

		return (
			<ListViewStyled>
				<RfxSupplierListHeader
					isDraft={isDraft}
					navigateCompareSelected={navigateCompareSelected}
					suppliersSelected={checkedRow?.length}
					totalSuppliers={suppliers?.length}
				/>
				<div ref={ref}>
					<Table
						containerClass={tw`w-full rounded-2xl rounded-t-none border-t border-gray-200`}
					>
						<TableHeader>
							<TableRow>
								<TableHead
									firstCol={true}
									className="!pl-6"
								>
									<div
										className="flex"
										style={{ gap: "8px", alignItems: "center" }}
									>
										<TableHeadComponent title="Company"></TableHeadComponent>
									</div>
								</TableHead>
								<TableHead>
									<TableHeadComponent title="Contact"></TableHeadComponent>
								</TableHead>
								<TableHead>
									<TableHeadComponent title="Primary Contact"></TableHeadComponent>
								</TableHead>
								<TableHead>
									<TableHeadComponent title="Location"></TableHeadComponent>
								</TableHead>
								<TableHead style={{ width: "10%" }}>
									<TableHeadComponent title="Last Updated"></TableHeadComponent>
								</TableHead>
								<TableHead style={{ width: "15%" }}>
									<TableHeadComponent title="Status"></TableHeadComponent>
								</TableHead>
								{!isResponseRestricted && (
									<TableHead>
										<TableHeadComponent
											title="Button View"
											className="hidden"
										/>
									</TableHead>
								)}
							</TableRow>
						</TableHeader>
						<TableBody>
							{(currPage <= 2 && isLoading) || loadList
								? [...Array(4)].map((_, index) => <SkeleteonRow key={index} />)
								: suppliers?.map((rfxInvite, index) => {
									const isGeneratingPdf = loadingIds.has(rfxInvite.id);
									return (
										<TableRow
											key={index}
											className={`${cn(
												rowSelected(rfxInvite?.company?.id) &&
												"[&>td]:bg-utility-blue-light-50/30"
											)} group`}
										>
											<TableCell
												firstCol={true}
												className="w-[30%] overflow-hidden !pl-6 group-hover:bg-utility-blue-light-50/30"
											>
												<div className="flex gap-4">
													<Checkbox
														disabled={
															isDraftSupplierNotSavedYet
																? false
																: rfxInvite?.company
																	? false
																	: true
														}
														onChange={(e) => checkHandler(e, rfxInvite)}
														checked={rowSelected(
															getCompanyObject(rfxInvite)?.id
														)}
														className="cursor-pointer"
													/>
													<div className="relative ml-3 flex items-center gap-2 text-black">
														{!rfxInvite?.is_guest ? (
															<Avatar
																avatar={getCompanyData(
																	rfxInvite,
																	"logo_path"
																)}
																size="lg"
																className="bg-white shadow-md"
															/>
														) : (
															<span className="relative justify-center overflow-hidden rounded-full bg-white !p-2 shadow-md">
																<Icon icon="guest-invite" />
															</span>
														)}
														<p className="title">
															{(() => {
																const companyObj =
																	getCompanyObject(rfxInvite);
																return !rfxInvite.is_guest
																	? companyObj?.name
																	: "Guest Invite";
															})()}
														</p>
													</div>
												</div>
											</TableCell>
											<TableCell className="min-w-[150px] !px-0 group-hover:bg-utility-blue-light-50/30">
												<Button
													btntype="secondary"
													className="!p-2"
													onClick={() => openContactModal(rfxInvite)}
												>
													<span className="text-[12px]">Contact details</span>
												</Button>
											</TableCell>
											<TableCell className="group-hover:bg-utility-blue-light-50/30">
												<p className="text">
													{getCompanyData(rfxInvite, "contact_name") ||
														getCompanyData(rfxInvite, "name") ||
														" - "}
												</p>
											</TableCell>
											<TableCell className="group-hover:bg-utility-blue-light-50/30">
												<p className="text">
													{(() => {
														const locations = getCompanyData(
															rfxInvite,
															"locations"
														);
														if (locations?.length > 0 && locations[0]) {
															return `${checkNoNull(locations[0]?.city)} ${locations[0]?.state}`;
														}
														return " - ";
													})()}
												</p>
											</TableCell>
											<TableCell className="group-hover:bg-utility-blue-light-50/30">
												<p className="text">
													{isDraftSupplierNotSavedYet
														? "Not saved yet"
														: rfxInvite?.lastUpdated
															? dateDiffChecker(rfxInvite?.lastUpdated)
															: "-"}
												</p>
											</TableCell>
											<TableCell className="group-hover:bg-utility-blue-light-50/30">
												{isDraftSupplierNotSavedYet ? (
													<StatusBadge
														status="INVITED"
														submittedDateTime={null}
														rfqStatus={rfqDetail?.status}
													/>
												) : (
													<StatusBadge
														status={rfxInvite?.status}
														submittedDateTime={rfxInvite?.submittedAt}
														rfqStatus={rfqDetail?.status}
													/>
												)}
											</TableCell>
											{!isResponseRestricted && (
												<TableCell className="w-[1/5] max-w-[240px] !px-0 group-hover:bg-utility-blue-light-50/30">
													<div className="flex justify-between gap-2">
														{!isDraftSupplierNotSavedYet &&
															isSubmitted(rfxInvite?.status) && (
																<div className="space-y-1">
																	<Button
																		btntype="secondary"
																		className={"!p-2"}
																		onClick={() =>
																			viewSubmissionHandler(rfxInvite)
																		}
																	>
																		<span className="text-[12px]">
																			View Response
																		</span>
																	</Button>
																	<Button
																		btntype="secondary"
																		className={`!p-2 ${isGeneratingPdf ? "cursor-wait" : ""}`}
																		onClick={() =>
																			downloadSubmission(rfxInvite)
																		}
																		isLoading={isGeneratingPdf}
																		disabled={isGeneratingPdf}
																	>
																		<span className="text-[12px]">
																			{isGeneratingPdf
																				? "Generating PDF Please Wait..."
																				: "Download Response"}
																		</span>
																	</Button>
																</div>
															)}
														<div>
															{!isDraftSupplierNotSavedYet &&
																isSubmitted(rfxInvite?.status) ? (
																<SubmissionEditButton
																	isSubmitted={isSubmitted(rfxInvite?.status)}
																	guestname={
																		getCompanyObject(rfxInvite)?.name || " - "
																	}
																	companystatus={rfxInvite?.status}
																	setTypeModal={setTypeModal}
																	onOpen={onOpen}
																	companyId={rfxInvite?.company?.id}
																	viewSubmission={() =>
																		viewSubmissionHandler(rfxInvite)
																	}
																	typeGuestName={typeGuestName}
																	email={rfxInvite?.email}
																	rfxStatus={rfxStatus}
																/>
															) : null}
														</div>
													</div>
												</TableCell>
											)}
										</TableRow>
									);
								})}
							{isLoading &&
								currPage > 2 &&
								[...Array(2)].map((_, index) => (
									<SkeleteonRow key={`loading-${index}`} />
								))}
							{!isDraft ? (
								<TableRow>
									<TableCell colSpan={6}>
										<Tooltip
											label="Browse the marketplace to add suppliers to your Supplier list"
											placement="top-start"
											hasArrow
										>
											<Button
												btntype="secondary"
												className="!text-xs"
												onClick={inviteNewSupplier}
											>
												<Flex
													align={"center"}
													gap={1}
												>
													<Icon
														icon="plus-square"
														color="#0031DD"
													/>
													<div>Invite Supplier</div>
												</Flex>
											</Button>
										</Tooltip>
									</TableCell>
								</TableRow>
							) : null}
						</TableBody>
					</Table>
				</div>
				<ContactDetailsModal
					isOpen={contactModalOpen}
					onClose={closeContactModal}
					company={selectedCompanyForContact}
				/>
			</ListViewStyled>
		);
	}
);

export default RFQListSubmission;
